import React, { useState, useEffect, useContext } from 'react';
import { AppBar, Toolbar, Typography, IconButton, Menu, MenuItem, Drawer, List, ListItem, Link, Badge, Tooltip } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { fetchAvatar, getAllUsers } from '../../services/userService';
import { UserContext } from '../../contexts/UserContext';
import defaultAvatar from '../../pic/avatar.png';
import './NavBar.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useTour } from '../UserGuide/TourProvider';
import GroupsIcon from '@mui/icons-material/Groups';

const NavBar = ({ setDrawerOpen, drawerOpen, handleMenuItemClick, surveyCompleted }) => {
    const { user, logout } = useContext(UserContext);
    const [anchorEl, setAnchorEl] = useState(null);
    const [avatar, setAvatar] = useState(defaultAvatar);
    const [pendingUsersCount, setPendingUsersCount] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const { startTour } = useTour();

    useEffect(() => {
        if (user && user._id) {
            loadAvatar(user._id);
            if (user.isAdmin) {
                fetchPendingUsersCount();
            }
        }
    }, [user]);

    const fetchPendingUsersCount = async () => {
        try {
            const usersData = await getAllUsers();
            const cutoffDate = new Date('2025-10-01');
            const pendingUsers = Array.isArray(usersData)
                ? usersData.filter(user =>
                    !user.isAdmin &&
                    (!user.studyGroup || user.studyGroup === 'unassigned') &&
                    new Date(user.createdAt) >= cutoffDate
                  )
                : [];
            setPendingUsersCount(pendingUsers.length);
        } catch (error) {
            console.error('Failed to fetch pending users count:', error);
            setPendingUsersCount(0);
        }
    };

    const loadAvatar = async (userId) => {
        try {
            const base64Image = await fetchAvatar(userId);
            if (base64Image) {
                setAvatar(`data:image/jpeg;base64,${base64Image}`);
            } else {
                // Keep default avatar if no avatar found
                setAvatar(defaultAvatar);
            }
        } catch (error) {
            console.error('Failed to load avatar:', error);
            // Keep default avatar on error
            setAvatar(defaultAvatar);
        }
    };

    const handleMenuItemClickWrapper = (item) => {
        handleMenuItemClick(item);

        if (location.pathname === '/') {
            if (user.isAdmin) {
                navigate('/admin-dashboard', { state: { initialContent: item } });
            } else {
                navigate('/dashboard', { state: { initialContent: item } });
            }
        }

        setDrawerOpen(false);
    };

    const handleLanguageChange = (language) => {
        i18n.changeLanguage(language);
    };

    const renderMenuItems = () => {
        // Check if user was created before October 1, 2025 (legacy user)
        const cutoffDate = new Date('2025-10-01');
        const userCreatedAt = new Date(user?.createdAt);
        const isLegacyUser = user?.createdAt && userCreatedAt < cutoffDate;

        // For unassigned users created after Oct 1, 2025, only show personal info
        if ((!user?.studyGroup || user?.studyGroup === 'unassigned' || user?.studyGroup === '') && !user?.isAdmin && !isLegacyUser) {
            return (
                <ListItem button onClick={() => handleMenuItemClickWrapper('personalInfo')} data-tour="personal-info">
                    <Typography variant="h6" style={{ fontFamily: 'Josefin Sans, sans-serif', fontSize: '15px' }}>
                        {t('navbar.personalInfo')}
                    </Typography>
                </ListItem>
            );
        }

        if (!surveyCompleted && !user?.isAdmin) {
            return (
                <>
                    <ListItem button onClick={() => handleMenuItemClickWrapper('surveyIntroduction')} data-tour="survey-introduction">
                        <Typography variant="h6" style={{ fontFamily: 'Josefin Sans, sans-serif', fontSize: '15px' }}>
                            {t('navbar.surveyIntroduction')}
                        </Typography>
                    </ListItem>
                    <ListItem button onClick={() => handleMenuItemClickWrapper('personalInfo')} data-tour="personal-info">
                        <Typography variant="h6" style={{ fontFamily: 'Josefin Sans, sans-serif', fontSize: '15px' }}>
                            {t('navbar.personalInfo')}
                        </Typography>
                    </ListItem>
                </>
            );
        }

        return (
            <>
                <ListItem button onClick={() => handleMenuItemClickWrapper('personalInfo')} data-tour="personal-info">
                    <Typography variant="h6" style={{ fontFamily: 'Josefin Sans, sans-serif', fontSize: '15px' }}>
                        {t('navbar.personalInfo')}
                    </Typography>
                </ListItem>
                {user?.isAdmin && (
                    <>
                        <ListItem button onClick={() => handleMenuItemClickWrapper('groupAssignment')} data-tour="group-assignment">
                            <Typography variant="h6" style={{ fontFamily: 'Josefin Sans, sans-serif', fontSize: '15px' }}>
                                {t('navbar.groupAssignment')}
                            </Typography>
                        </ListItem>
                        <ListItem button onClick={() => handleMenuItemClickWrapper('userList')} data-tour="user-list">
                            <Typography variant="h6" style={{ fontFamily: 'Josefin Sans, sans-serif', fontSize: '15px' }}>
                                {t('navbar.userList')}
                            </Typography>
                        </ListItem>
                        <ListItem button onClick={() => handleMenuItemClickWrapper('setNotification')} data-tour="set-notification">
                            <Typography variant="h6" style={{ fontFamily: 'Josefin Sans, sans-serif', fontSize: '15px' }}>
                                {t('navbar.setNotification')}
                            </Typography>
                        </ListItem>
{/* 
                        <ListItem button onClick={() => handleMenuItemClickWrapper('documentation')} data-tour="documentation">
                            <Typography variant="h6" style={{ fontFamily: 'Josefin Sans, sans-serif', fontSize: '15px' }}>
                                {t('navbar.documentation')}
                            </Typography>
                        </ListItem> */}
                    </>
                )}
                {!user?.isAdmin && (
                    <>
                        {/* For control group users who completed survey, show personal info and survey results */}
                        {user?.studyGroup === 'control' && surveyCompleted ? (
                            <>
                                <ListItem button onClick={() => handleMenuItemClickWrapper('surveyResults')} data-tour="survey-results">
                                    <Typography variant="h6" style={{ fontFamily: 'Josefin Sans, sans-serif', fontSize: '15px' }}>
                                        {t('navbar.surveyResults') || 'Survey Results'}
                                    </Typography>
                                </ListItem>
                            </>
                        ) : (
                            /* For experimental group or users who haven't completed survey, show all items */
                            <>
                                <ListItem button onClick={() => handleMenuItemClickWrapper('overview')} data-tour="overview">
                                    <Typography variant="h6" style={{ fontFamily: 'Josefin Sans, sans-serif', fontSize: '15px' }}>
                                        {t('navbar.overview')}
                                    </Typography>
                                </ListItem>
                                <ListItem button onClick={() => handleMenuItemClickWrapper('DailySurvey')} data-tour="daily-survey">
                                    <Typography variant="h6" style={{ fontFamily: 'Josefin Sans, sans-serif', fontSize: '15px' }}>
                                        {t('navbar.dailySurvey')}
                                    </Typography>
                                </ListItem>
                                <ListItem button onClick={() => handleMenuItemClickWrapper('performanceQuestion')} data-tour="performance-question">
                                    <Typography variant="h6" style={{ fontFamily: 'Josefin Sans, sans-serif', fontSize: '15px' }}>
                                        {t('navbar.performanceQuestion')}
                                    </Typography>
                                </ListItem>
                                <ListItem button onClick={() => handleMenuItemClickWrapper('calendar')} data-tour="calendar">
                                    <Typography variant="h6" style={{ fontFamily: 'Josefin Sans, sans-serif', fontSize: '15px' }}>
                                        {t('navbar.calendar')}
                                    </Typography>
                                </ListItem>
                                <ListItem button onClick={() => handleMenuItemClickWrapper('cognitivePatterns')} data-tour="cognitive-patterns">
                                    <Typography variant="h6" style={{ fontFamily: 'Josefin Sans, sans-serif', fontSize: '15px' }}>
                                        {t('navbar.cognitivePatterns') || 'Cognitive Patterns'}
                                    </Typography>
                                </ListItem>
                                <ListItem button onClick={() => handleMenuItemClickWrapper('bodyMetrics')} data-tour="body-metrics">
                                    <Typography variant="h6" style={{ fontFamily: 'Josefin Sans, sans-serif', fontSize: '15px' }}>
                                        {t('navbar.bodyMetrics')}
                                    </Typography>
                                </ListItem>
                                <ListItem button onClick={() => handleMenuItemClickWrapper('result')} data-tour="result">
                                    <Typography variant="h6" style={{ fontFamily: 'Josefin Sans, sans-serif', fontSize: '15px' }}>
                                        {t('navbar.result')}
                                    </Typography>
                                </ListItem>
                            </>
                        )}
                    </>
                )}
            </>
        );
    };

    return (
        <>
            <AppBar position="fixed" className="navbar">
                <Toolbar>
                    <IconButton edge="start" color="inherit" aria-label="menu" onClick={() => setDrawerOpen(true)}>
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" style={{ flexGrow: 1, fontFamily: 'Dosis, sans-serif' }}>
                        UFLOW
                    </Typography>
                    <IconButton
                        onClick={startTour}
                        color="inherit"
                        sx={{ mr: 1 }}
                        title={t('navbar.startTour')}
                    >
                        <MenuBookIcon />
                    </IconButton>
                    <div>
                        <Link href="/" className="navbar-link">{t('navbar.home')}</Link>
                    </div>
                    {user?.isAdmin && pendingUsersCount > 0 && (
                        <Tooltip title={t('navbar.pendingAssignments', { count: pendingUsersCount })}>
                            <IconButton
                                onClick={() => handleMenuItemClickWrapper('groupAssignment')}
                                color="inherit"
                                sx={{ ml: 1, mr: 1 }}
                            >
                                <Badge badgeContent={pendingUsersCount} color="error">
                                    <GroupsIcon />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                    )}
                    {user && (
                        <div
                            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                        >
                            <img
                                src={avatar}
                                alt="User Avatar"
                                className="navbar-img"
                            />
                            <Typography variant="body1" style={{ marginLeft: '10px' }}>
                                {t('navbar.hi')}, {user.username}!
                            </Typography>
                            {/* Language Switcher */}
                            <div style={{ display: 'flex', alignItems: 'center', marginLeft: '20px' }}>
                                <Typography
                                    variant="body2"
                                    onClick={() => handleLanguageChange('en')}
                                    style={{
                                        cursor: 'pointer',
                                        fontWeight: i18n.language === 'en' ? 'bold' : 'normal',
                                        textDecoration: i18n.language === 'en' ? 'underline' : 'none',
                                        marginRight: '10px'
                                    }}
                                >
                                    EN
                                </Typography>
                                <Typography
                                    variant="body2"
                                    onClick={() => handleLanguageChange('de')}
                                    style={{
                                        cursor: 'pointer',
                                        fontWeight: i18n.language === 'de' ? 'bold' : 'normal',
                                        textDecoration: i18n.language === 'de' ? 'underline' : 'none'
                                    }}
                                >
                                    DE
                                </Typography>
                            </div>
                        </div>
                    )}
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => setAnchorEl(null)}
                    >
                        <MenuItem onClick={logout}>{t('navbar.logout')}</MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>
            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{ style: { height: '100vh', zIndex: 1100 } }}
            >
                <div className="menu">
                    <List>
                        {renderMenuItems()}
                    </List>
                </div>
            </Drawer>
        </>
    );
};

export default NavBar;
